import { DashCard } from './DashboardCards'
import { attention, ATTENTION_CAPTION, topPart, type AttentionScore } from '../../../lib/attention'
import { prettyName } from '../../../lib/mandal-name'

import type { MandalAggregate } from '../../../api/types'

const TOP_N = 5

interface AttentionListProps {
  mandals: MandalAggregate[]
  /** Click a row → toggle that mandal into the page scope (the dashboard + map re-scope in place;
   *  the boards inherit the scope when opened via the header chevron). */
  onPick: (name: string) => void
  onOpen: () => void
}

/**
 * "Needs attention" — the top-N mandals by the canonical Attention Score, as ranked magnitude bars
 * (one hue; the score is the single axis) with the open/critical counts spelled out beside each.
 * The same score shades the Overview map, so the list and the choropleth can never disagree.
 */
export function AttentionList({ mandals, onPick, onOpen }: AttentionListProps) {
  const ranked: AttentionScore[] = mandals
    .map(attention)
    .filter((a) => a.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, TOP_N)
  const max = Math.max(1, ...ranked.map((a) => a.score))

  return (
    <DashCard title="Needs attention" caption="Top mandals by attention score" onOpen={onOpen} openLabel="Open Issues board">
      {ranked.length === 0 ? (
        <div className="py-4 text-center text-[11.5px] text-gis-soft">
          Nothing needs attention in this selection.
        </div>
      ) : (
        <div className="flex flex-col gap-[7px]">
          {ranked.map((a) => (
            <button
              key={a.mandal}
              type="button"
              onClick={() => onPick(a.mandal)}
              title={`Scope the dashboard and map to ${prettyName(a.mandal)} (click again to clear)`}
              className="-mx-1 grid cursor-pointer grid-cols-[minmax(0,128px)_minmax(0,1fr)_auto] items-center gap-3 rounded-[8px] px-1 py-[3px] text-left transition-colors hover:bg-[#f5f7fa] focus-visible:ring-2 focus-visible:ring-blue-500/60"
            >
              <span className="min-w-0">
                <span className="block truncate text-[12px] font-semibold text-gis-ink-2">
                  {prettyName(a.mandal)}
                  {a.thin && <span className="ml-1 align-middle text-[9px] font-medium text-gis-muted">(thin data)</span>}
                </span>
                {/* WHY this mandal ranks — its biggest single point contributor. */}
                {topPart(a) && (
                  <span className="block truncate text-[10px] text-gis-soft">top driver · {topPart(a)!.label}</span>
                )}
              </span>
              <span className="flex h-[10px] min-w-0 overflow-hidden rounded-full bg-gis-track-2">
                <span
                  className="block h-full rounded-full bg-gis-red transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
                  style={{ width: `${(a.score / max) * 100}%` }}
                />
              </span>
              <span className="flex items-center gap-2 text-[10.5px] tabular-nums">
                <span className="min-w-[26px] text-right font-bold text-gis-ink">{a.score}</span>
                <span className="text-gis-soft">
                  {a.openIssue} open{a.critical > 0 ? ` · ${a.critical} critical` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      <p className="mt-2 text-[9.5px] leading-snug text-gis-muted">{ATTENTION_CAPTION}</p>
    </DashCard>
  )
}
